import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { componentRegistry } from '../../config/componentRegistry';
import { loadPageDefinition, fetchSectionProps } from '../../services/pageLoader';
import LandingLowerSeal from '../../components/LandingLowerSeal/LandingLowerSeal';
import { LandingFaqCtaBand } from '../../components/LandingFaqCtaBand';
import { PageLoadScreen } from '../../components/library/loading/loading';
import { useLocale } from '../../locale/LocaleProvider';
import { applyPageLocale } from '../../locale/applyPageLocale';
const EMPTY_PROPS = {};
const SectionRenderer = ({ section }) => {
    const [fetchedProps, setFetchedProps] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(!!section.api);
    /** Sections without `api` must read `section.props` on every render so locale overlays apply immediately (no stale useState). */
    const staticProps = section.props ?? EMPTY_PROPS;
    useEffect(() => {
        if (!section.api)
            return;
        let cancelled = false;
        (async () => {
            try {
                const merged = await fetchSectionProps(section.api, staticProps);
                if (!cancelled)
                    setFetchedProps(merged);
            }
            catch (e) {
                if (!cancelled)
                    setError(e instanceof Error ? e.message : 'API error');
            }
            finally {
                if (!cancelled)
                    setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [section.api, section.props]);
    const Component = componentRegistry[section.component];
    if (!Component) {
        return (_jsxs("div", { className: "cms-section-error", "data-component": section.component, children: ["Unknown component: ", section.component] }));
    }
    if (error) {
        return (_jsx("div", { className: "cms-section-error", "data-component": section.component, children: error }));
    }
    if (section.api && loading) {
        return (_jsx("div", { id: section.key ?? undefined, "data-section-key": section.key ?? '', "data-component": section.component, children: _jsx(PageLoadScreen, { variant: "embedded" }) }));
    }
    const props = section.api ? (fetchedProps ?? staticProps) : staticProps;
    return (_jsx("div", { id: section.key ?? undefined, "data-section-key": section.key ?? '', children: _jsx(Component, { ...props }, section.key ?? section.component) }));
};
const PageRenderer = ({ pagePath }) => {
    const { locale } = useLocale();
    const [page, setPage] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const localizedPage = useMemo(() => (page ? applyPageLocale(page, locale) : null), [page, locale]);
    useEffect(() => {
        let cancelled = false;
        loadPageDefinition(pagePath)
            .then((def) => {
            if (!cancelled) {
                setPage(def);
            }
        })
            .catch((e) => {
            if (!cancelled)
                setError(e instanceof Error ? e.message : 'Failed to load page');
        })
            .finally(() => {
            if (!cancelled)
                setLoading(false);
        });
        return () => { cancelled = true; };
    }, [pagePath]);
    useEffect(() => {
        if (localizedPage?.title && typeof document !== 'undefined') {
            document.title = localizedPage.title;
        }
    }, [localizedPage?.title]);
    if (loading) {
        return (_jsx("main", { id: "main-content", tabIndex: -1, className: "cms-page cms-page--loading", "aria-busy": "true", children: _jsx(PageLoadScreen, { variant: "fullscreen" }) }));
    }
    if (error)
        return _jsx("div", { className: "cms-page-error", children: error });
    if (!localizedPage)
        return null;
    const renderSections = () => {
        const list = localizedPage.sections;
        const nodes = [];
        let i = 0;
        while (i < list.length) {
            const s = list[i];
            const next = list[i + 1];
            const isFaqCtaPair = localizedPage.id === 'landing' &&
                s.key === 'faq' &&
                s.component === 'AccordionBlock' &&
                next &&
                next.key === 'cta' &&
                next.component === 'CtaBlock';
            if (isFaqCtaPair) {
                nodes.push(_jsxs(LandingFaqCtaBand, { children: [_jsx(SectionRenderer, { section: s }, `${s.key}-${locale}`), _jsx(SectionRenderer, { section: next }, `${next.key}-${locale}`)] }, `landing-faq-cta-band-${locale}`));
                i += 2;
            }
            else {
                nodes.push(_jsx(SectionRenderer, { section: s }, `${s.key ?? `${s.component}-${i}`}-${locale}`));
                i += 1;
            }
        }
        return _jsx(_Fragment, { children: nodes });
    };
    const content = renderSections();
    const isLanding = localizedPage.id === 'landing';
    return (_jsx("main", { id: "main-content", tabIndex: -1, className: `cms-page${isLanding ? ' cms-page--landing' : ''}`, "data-page-id": localizedPage.id, children: localizedPage.layout === 'DefaultLayout' ? (_jsxs("div", { className: "cms-page__layout", children: [isLanding ? _jsx(LandingLowerSeal, {}) : null, content] })) : (content) }));
};
export default PageRenderer;
